import { formatAmount, formatShareBps, USD } from "@loot-os/api-contract";
import type { ShopifyAdminAppRouter } from "@loot-os/functions/src/dashboard-api/trpc/shopifyAdmin/router.js";
import { useMutation } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { useTRPC } from "./trpc";

/** The shop's Loot Boxes plan, as `status` answers it for a shop set up with a tenant. */
export type Billing = NonNullable<inferRouterOutputs<ShopifyAdminAppRouter>["status"]["billing"]>;
export type PlanStatus = Billing["status"];

/** The plan statuses the merchant still has to approve: none yet, waiting, or turned down. */
export type PlanApprovalStatus = Exclude<PlanStatus, "active" | "frozen">;

/**
 * Whether the merchant has approved the plan: active, or frozen by Shopify until the store's bill
 * is paid. Frozen is a standing condition the banner reports, not a setup step to do again.
 */
export function isPlanApproved(status: PlanStatus): status is "active" | "frozen" {
  return status === "active" || status === "frozen";
}

/** What asking for the plan says, in the banner and in Home's setup guide. */
export interface PlanApprovalCopy {
  heading: string;
  body: string;
  action: string;
}

const APPROVAL_HEADINGS: Record<PlanApprovalStatus, string> = {
  none: "Approve the Loot Boxes plan to start selling",
  pending: "Finish approving the Loot Boxes plan",
  declined: "The Loot Boxes plan was declined",
  cancelled: "The Loot Boxes plan was cancelled",
  expired: "The Loot Boxes plan request expired",
};

/**
 * The plan's terms, and what waits on it: creating boxes (with the rest of Home's setup guide),
 * and selling the ones already made.
 */
export function planApprovalCopy(status: PlanApprovalStatus, feeBps: number): PlanApprovalCopy {
  const fee = formatShareBps(feeBps);
  const waiting =
    status === "none" || status === "pending" ? "" : " Until then, your boxes show sold out.";
  return {
    heading: APPROVAL_HEADINGS[status],
    body: `Loot Boxes is free to set up. When a shopper claims a box, sells it back or moves it to store credit, Shopify bills you ${fee} of what they paid for it, on your regular Shopify invoice. Boxes can be created and sold only once the plan is approved.${waiting}`,
    action: status === "pending" ? "Approve plan" : "Review plan",
  };
}

/**
 * The rate the merchant is billed at: the lesser of the rate they approved and the fee now, as
 * core's `billedRateBps` bills it (a cut applies at once, a raise waits for approval).
 */
export function billedRateBps(billing: Pick<Billing, "approvedRateBps" | "feeBps">): number {
  return Math.min(billing.approvedRateBps ?? billing.feeBps, billing.feeBps);
}

/** What the setup guide says of an approved plan: the rate billed, and the spending limit. */
export function approvedPlanCopy(
  billing: Pick<Billing, "approvedRateBps" | "feeBps" | "cappedAmountCents">,
): string {
  const rate = formatShareBps(billedRateBps(billing));
  const limit =
    billing.cappedAmountCents == null
      ? ""
      : `, up to ${formatAmount(billing.cappedAmountCents, USD)} every 30 days`;
  return `Approved. When a shopper claims a box, sells it back or moves it to store credit, Shopify bills you ${rate} of what they paid for it, on your regular Shopify invoice${limit}.`;
}

/** What the spending-limit banner says, or null while the plan is clear of its limit. */
export function spendingLimitCopy(
  billing: Pick<Billing, "status" | "cappedAmountCents" | "capWarnedAt" | "capReachedAt">,
): PlanApprovalCopy | null {
  if (!isPlanApproved(billing.status) || billing.cappedAmountCents == null) return null;
  const limit = formatAmount(billing.cappedAmountCents, USD);
  const waiting =
    "Your boxes stay on sale; fees past the limit are billed once you raise it, or when the next 30-day cycle begins.";
  if (billing.capReachedAt) {
    return {
      heading: "Loot Boxes reached its spending limit",
      body: `Shopify bills Loot Boxes at most ${limit} every 30 days, and this cycle has reached it. ${waiting}`,
      action: "Raise limit",
    };
  }
  if (billing.capWarnedAt) {
    return {
      heading: "Loot Boxes is close to its spending limit",
      body: `Shopify has billed 90% of the ${limit} Loot Boxes can bill every 30 days. ${waiting}`,
      action: "Raise limit",
    };
  }
  return null;
}

/**
 * Asks Shopify to raise the plan's spending limit (`billing.raiseCap`, twice the current one) and
 * opens its approval page at the top level, like {@link usePlanApproval}.
 */
export function useSpendingLimitRaise() {
  const trpc = useTRPC();
  return useMutation(
    trpc.billing.raiseCap.mutationOptions({
      onSuccess: ({ confirmationUrl }) => {
        open(confirmationUrl, "_top");
      },
    }),
  );
}

/**
 * Asks Shopify for the plan (`billing.start`) and opens its approval page at the top level;
 * Shopify returns the merchant to the app, which then reads the plan back.
 */
export function usePlanApproval() {
  const trpc = useTRPC();
  return useMutation(
    trpc.billing.start.mutationOptions({
      onSuccess: ({ confirmationUrl }) => {
        open(confirmationUrl, "_top");
      },
    }),
  );
}
